// inventory/unsaved-preview.ts — tiles for rows with no saved inventory record.
//
// ft_inventory_tile_mount() previews authored gear that was never saved as an inventory row (an authored creature's
// equipped gear in the editor's Equipment Slots Preview, a character-creator row). PHP ships only the row's identity —
// `{ _inventory_refusal: 'unported:unsaved-inventory-preview', preview: { definition_id, item_qty, enchantment_template_id } }`
// — and the tile renders from the authored definition read through the page's definition store (one batched
// object_inc.php read, per-viewer redaction on the server). As before the C4 flip, the tile is the base definition with the
// preview's quantity and enchantment id; a definition the viewer may not read, or a failed read, leaves a named refusal.

import { sharedDefinitionStore } from '../janus-engine/store/definition-store';

type Row = Record<string, unknown>;
export const UNSAVED_INVENTORY_PREVIEW = 'unported:unsaved-inventory-preview';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const record = (value: unknown): value is Row => value !== null && typeof value === 'object' && !Array.isArray(value);

export interface UnsavedInventoryPreview { definitionId: string; qty: unknown; enchantmentTemplateId: string }

/** The preview identity PHP serves for an unsaved row; null for anything else. */
export function unsavedInventoryPreview(value: unknown): UnsavedInventoryPreview | null {
    if (!record(value) || value._inventory_refusal !== UNSAVED_INVENTORY_PREVIEW || !record(value.preview)) return null;
    const definitionId = String(value.preview.definition_id ?? '');
    if (!UUID.test(definitionId)) return null;
    const enchantment = String(value.preview.enchantment_template_id ?? '');
    return { definitionId: definitionId.toUpperCase(), qty: value.preview.item_qty ?? 1,
        enchantmentTemplateId: UUID.test(enchantment) ? enchantment : '' };
}

/** The tile row: the authored definition plus the preview's own inventory bucket (no saved id, as the legacy preview). */
export function unsavedPreviewRow(definition: Row, preview: UnsavedInventoryPreview): Row {
    return { ...definition, inventory: { inventory_id: null, inventory_item_id: preview.definitionId, item_qty: preview.qty,
        ...(preview.enchantmentTemplateId ? { enchantment_template_id: preview.enchantmentTemplateId } : {}) } };
}

export interface PendingPreviewTile { mount: HTMLElement; preview: UnsavedInventoryPreview; render(row: Row): void }

/** One batched definition read for every preview tile of a scan; each tile renders or keeps a named refusal. */
export async function paintUnsavedPreviewTiles(tiles: readonly PendingPreviewTile[],
    store = sharedDefinitionStore(BASE_URL)): Promise<void> {
    if (!tiles.length) return;
    let definitions: Awaited<ReturnType<typeof store.getDefinitions>>;
    try { definitions = await store.getDefinitions(tiles.map(tile => tile.preview.definitionId)); }
    catch (error) {
        console.warn('[inventory] unsaved preview definitions', error);
        for (const tile of tiles) tile.mount.textContent = `${UNSAVED_INVENTORY_PREVIEW}:definition-read`;
        return;
    }
    for (const tile of tiles) {
        const definition = definitions.get(tile.preview.definitionId)?.def;
        if (!definition) {
            tile.mount.textContent = `${UNSAVED_INVENTORY_PREVIEW}:def-${store.refusal(tile.preview.definitionId) ?? 'missing'}`;
            continue;
        }
        try { tile.render(unsavedPreviewRow(definition, tile.preview)); }
        catch (error) { console.warn('[inventory] unsaved preview tile', error); tile.mount.textContent = `${UNSAVED_INVENTORY_PREVIEW}:render`; }
    }
}
